import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaChevronDown, FaSearch, FaQuestionCircle } from 'react-icons/fa';
import './faq.css';

// Animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      ease: "easeOut"
    }
  }
};

const accordionVariants = {
  closed: {
    height: 0,
    opacity: 0,
    transition: {
      duration: 0.3,
      ease: "easeInOut"
    }
  },
  open: {
    height: "auto",
    opacity: 1,
    transition: {
      duration: 0.4,
      ease: "easeOut"
    }
  }
};

function FAQ() {
  const [activeCategory, setActiveCategory] = useState('general');
  const [searchTerm, setSearchTerm] = useState('');
  const [openItems, setOpenItems] = useState({});

  const faqData = {
    general: [
      {
        question: "What is the best time to visit Sri Lanka?",
        answer: "The best time to visit Sri Lanka depends on which part of the island you plan to explore. The west and south coasts are ideal from December to March, while the east coast is best from April to September. The cultural triangle can be visited year-round."
      },
      {
        question: "Do I need a visa to visit Sri Lanka?",
        answer: "Yes, most nationalities require an Electronic Travel Authorization (ETA) before arrival. You can apply online through the official Sri Lankan immigration website. The process is straightforward and usually approved within 24-48 hours."
      },
      {
        question: "What is the local currency and can I use credit cards?",
        answer: "The local currency is the Sri Lankan Rupee (LKR). Credit cards are widely accepted in hotels, restaurants, and larger shops in cities. However, it's recommended to carry cash for local markets, small shops, and rural areas."
      },
      {
        question: "Is Sri Lanka safe for tourists?",
        answer: "Sri Lanka is generally very safe for tourists. The locals are known for their hospitality and friendliness towards visitors. However, as with any travel destination, it's wise to take standard precautions with your belongings and avoid isolated areas at night."
      }
    ],
    booking: [
      {
        question: "How far in advance should I book my tour?",
        answer: "We recommend booking at least 2-3 months in advance, especially for peak season (December to April). For custom tours and special requirements, 4-6 months in advance is ideal to ensure availability of preferred accommodations and guides."
      },
      {
        question: "What is your deposit and payment policy?",
        answer: "We require a 30% deposit to confirm your booking, with the balance due 30 days before arrival. For bookings made within 30 days of travel, full payment is required. We accept bank transfers, credit cards, and online payments."
      },
      {
        question: "Can I modify my booking after confirmation?",
        answer: "Yes, modifications are possible subject to availability. Changes made more than 30 days before arrival are free of charge. Changes within 30 days may incur additional fees depending on the modifications required."
      },
      {
        question: "Do you offer group discounts?",
        answer: "Yes, we offer special rates for groups of 6 or more people. Group discounts range from 10-25% depending on group size, season, and tour package. Contact us for custom group pricing."
      }
    ],
    tours: [
      {
        question: "What types of accommodations do you offer?",
        answer: "We offer a range of accommodations from luxury 5-star hotels and boutique villas to comfortable 3-star hotels and eco-lodges. All our accommodations are carefully selected for quality, location, and authentic Sri Lankan hospitality."
      },
      {
        question: "Are your tours private or in groups?",
        answer: "We specialize in private tours tailored to your preferences. However, we also offer small group tours (maximum 12 people) for those who prefer to travel with others. Private tours offer more flexibility and personalization."
      },
      {
        question: "What is included in your tour packages?",
        answer: "Our packages typically include accommodation, private vehicle with driver, expert local guides, breakfast daily, specified activities and entrance fees, and 24/7 support. International flights, visas, and personal expenses are not included."
      },
      {
        question: "Can I customize my tour itinerary?",
        answer: "Absolutely! We specialize in creating personalized itineraries. You can customize everything from destinations and activities to accommodation preferences and pace of travel. Our travel experts will work with you to create your perfect Sri Lankan adventure."
      }
    ],
    practical: [
      {
        question: "What should I pack for Sri Lanka?",
        answer: "Lightweight clothing, comfortable walking shoes, swimwear, sunscreen, insect repellent, hat, and modest clothing for temple visits (shoulders and knees covered). A light jacket is recommended for hill country evenings."
      },
      {
        question: "What is the food like in Sri Lanka?",
        answer: "Sri Lankan cuisine is flavorful and diverse, featuring rice, curry, fresh seafood, and tropical fruits. Popular dishes include hoppers, kottu roti, and string hoppers. Most dietary requirements can be accommodated with advance notice."
      },
      {
        question: "Is English widely spoken in Sri Lanka?",
        answer: "Yes, English is widely spoken in tourist areas, hotels, and by our guides. In rural areas, you might find fewer English speakers, but our guides will always be there to assist with communication."
      },
      {
        question: "What is the voltage and plug type in Sri Lanka?",
        answer: "Sri Lanka uses 230V/50Hz. The plug types are D (three round pins in triangle pattern) and G (three rectangular pins). Most hotels provide adapters, but it's recommended to bring a universal travel adapter."
      }
    ]
  };

  const categories = [
    { id: 'general', name: 'General Information', icon: '🌍' },
    { id: 'booking', name: 'Booking & Payments', icon: '💳' },
    { id: 'tours', name: 'Tours & Services', icon: '🚗' },
    { id: 'practical', name: 'Practical Tips', icon: '🎒' }
  ];

  const toggleItem = (category, index) => {
    setOpenItems(prev => ({
      ...prev,
      [`${category}-${index}`]: !prev[`${category}-${index}`]
    }));
  };

  const filteredFAQs = faqData[activeCategory].filter(item =>
    item.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.answer.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="faq-page">
      {/* Hero Section */}
      <section className="faq-hero">
        <div className="faq-container">
          <motion.div 
            className="faq-hero-content"
            initial="hidden"
            animate="visible"
            variants={containerVariants}
          >
            <motion.div className="faq-hero-text" variants={itemVariants}>
              <motion.h1 
                className="faq-hero-title"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.2 }}
              >
                Frequently Asked Questions
              </motion.h1>
              <motion.p 
                className="faq-hero-description"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.4 }}
              >
                Find answers to common questions about traveling to Sri Lanka with Alina Lanka Tours. 
                Can't find what you're looking for? Contact us directly.
              </motion.p>
            </motion.div>
            <motion.div 
              className="faq-hero-icon"
              variants={itemVariants}
              animate={{ 
                y: [0, -10, 0],
                rotate: [0, 5, -5, 0]
              }}
              transition={{
                duration: 4,
                repeat: Infinity,
                ease: "easeInOut"
              }}
            >
              <FaQuestionCircle />
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Search Section */}
      <section className="faq-search-section">
        <div className="faq-container">
          <motion.div 
            className="faq-search-container"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.6 }}
          >
            <div className="faq-search-box">
              <FaSearch className="faq-search-icon" />
              <input
                type="text"
                placeholder="Search questions or keywords..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="faq-search-input"
              />
            </div>
            <p className="faq-search-help">
              Search through {Object.values(faqData).flat().length} frequently asked questions
            </p>
          </motion.div>
        </div>
      </section>

      {/* Categories & FAQ Content */}
      <section className="faq-content-section">
        <div className="faq-container">
          <div className="faq-layout">
            {/* Categories Sidebar */}
            <motion.div 
              className="faq-categories-sidebar"
              initial="hidden"
              animate="visible"
              variants={containerVariants}
            >
              <h3 className="faq-categories-title">Categories</h3>
              <div className="faq-categories-list">
                {categories.map((category) => (
                  <motion.button
                    key={category.id}
                    className={`faq-category-btn ${activeCategory === category.id ? 'active' : ''}`}
                    onClick={() => setActiveCategory(category.id)}
                    variants={itemVariants}
                    whileHover={{ scale: 1.05, x: 5 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <span className="faq-category-icon">{category.icon}</span>
                    <span className="faq-category-name">{category.name}</span>
                    <span className="faq-category-count">
                      {faqData[category.id].length}
                    </span>
                  </motion.button>
                ))}
              </div>
            </motion.div>

            {/* FAQ Items */}
            <motion.div 
              className="faq-items-container"
              key={activeCategory}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
            >
              <div className="faq-category-header">
                <h2 className="faq-category-title">
                  {categories.find(cat => cat.id === activeCategory)?.name}
                </h2>
                <p className="faq-category-description">
                  {filteredFAQs.length} questions found
                  {searchTerm && ` for "${searchTerm}"`}
                </p>
              </div>

              <div className="faq-accordion">
                {filteredFAQs.length > 0 ? (
                  filteredFAQs.map((item, index) => (
                    <motion.div
                      key={index}
                      className="faq-accordion-item"
                      variants={itemVariants}
                      initial="hidden"
                      animate="visible"
                      transition={{ delay: index * 0.1 }}
                    >
                      <motion.button
                        className={`faq-accordion-question ${openItems[`${activeCategory}-${index}`] ? 'active' : ''}`}
                        onClick={() => toggleItem(activeCategory, index)}
                        whileHover={{ backgroundColor: 'rgba(165, 156, 80, 0.05)' }}
                        whileTap={{ scale: 0.98 }}
                      >
                        <span className="faq-question-text">{item.question}</span>
                        <motion.div
                          className="faq-accordion-icon"
                          animate={{ rotate: openItems[`${activeCategory}-${index}`] ? 180 : 0 }}
                          transition={{ duration: 0.3 }}
                        >
                          <FaChevronDown />
                        </motion.div>
                      </motion.button>
                      <AnimatePresence>
                        {openItems[`${activeCategory}-${index}`] && (
                          <motion.div
                            className="faq-accordion-answer"
                            variants={accordionVariants}
                            initial="closed"
                            animate="open"
                            exit="closed"
                          >
                            <div className="faq-answer-content">
                              <p>{item.answer}</p>
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </motion.div>
                  ))
                ) : (
                  <motion.div
                    className="faq-no-results"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                  >
                    <FaSearch className="faq-no-results-icon" />
                    <h3>No questions found</h3>
                    <p>Try searching with different keywords or browse other categories</p>
                  </motion.div>
                )}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="faq-cta-section">
        <div className="faq-container">
          <motion.div 
            className="faq-cta-content"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="faq-cta-title">Still have questions?</h2>
            <p className="faq-cta-description">
              Our travel experts are here to help you plan your perfect Sri Lankan adventure. 
              Contact us for personalized assistance.
            </p>
            <div className="faq-cta-buttons">
              <motion.button 
                className="faq-btn faq-btn-primary"
                whileHover={{ scale: 1.05, y: -2 }}
                whileTap={{ scale: 0.95 }}
              >
                Contact Us
              </motion.button>
              <motion.button 
                className="faq-btn faq-btn-secondary"
                whileHover={{ scale: 1.05, y: -2 }}
                whileTap={{ scale: 0.95 }}
              >
                View All Tours
              </motion.button>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}

export default FAQ;